import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { EsnIconName } from '../../../shared/icons';
import type { EsnIconButtonFill, EsnIconButtonSize } from '../../../shared/types/atoms';
import { IconComponent } from '../icon/icon.component';

/**
 * A glyph-only action (overflow menu, close, zoom) with a mandatory accessible name,
 * so icon-only controls are never silent to screen readers.
 */
@Component({
  selector: 'esn-icon-button',
  imports: [IconComponent],
  templateUrl: './icon-button.component.html',
  styleUrl: './icon-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconButtonComponent {
  readonly icon = input.required<EsnIconName>();

  /**
   * Accessible name (also the tooltip) — the button has no visible text.
   */
  readonly label = input.required<string>();

  readonly size = input<EsnIconButtonSize>(32);
  readonly fill = input<EsnIconButtonFill>('filled');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);

  /**
   * Set when the button opens a popup (e.g. an actions menu): mirrored to `aria-haspopup` / `aria-expanded`.
   */
  readonly haspopup = input<'menu' | 'dialog' | 'listbox' | null>(null);
  readonly expanded = input<boolean | null>(null);

  readonly clicked = output<MouseEvent>();

  protected readonly classes = computed(() => `esn-icon-button esn-icon-button--${this.size()} esn-icon-button--${this.fill()}`);

  protected handleClick(event: MouseEvent): void {
    if (!this.disabled()) this.clicked.emit(event);
  }
}
